import { Link } from "react-router-dom";
import { Instagram, Mail, Linkedin, ArrowUpRight, Heart } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    community: [
      { label: "Member Directory", href: "/members" },
      { label: "Events", href: "/events" },
      { label: "Community", href: "/community" },
      { label: "Resources", href: "/blog" },
    ],
    company: [
      { label: "About Us", href: "/#about" },
      { label: "Membership", href: "/#membership" },
      { label: "Support Us", href: "https://ko-fi.com/creativeensemblemedia", external: true },
      { label: "Contact", href: "/contact" },
    ],
    legal: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
    ],
  };

  const socialLinks = [
    { icon: Instagram, href: "https://instagram.com/thecreativeensemble", label: "Instagram" },
    { icon: Linkedin, href: "https://linkedin.com/company/thecreativeensemble", label: "LinkedIn" },
    { icon: Mail, href: "mailto:hello@thecreativeensemble.com", label: "Email" },
  ];

  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="container-main py-20 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-5">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-logo uppercase leading-tight mb-6" style={{letterSpacing: '-0.03em', wordSpacing: '-0.05em'}}>
              THE <span className="text-[#FF2D87]">CREATIVE</span><br />
              ENSEMBLE
            </h2>
            <p className="text-[#A0A0A0] text-base max-w-md mb-8">
              A curated creative and professional network for female and gender diverse creatives and professionals combining community, collaboration, education, and opportunity.
            </p>
            <p className="text-[#FFC2E8] font-display text-xl">
              We don't compete. We connect.
            </p>
          </div>

          {/* Links */}
          <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h3 className="overline text-[#FF2D87] mb-6">Community</h3>
              <ul className="space-y-4">
                {footerLinks.community.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="footer-link text-[#A0A0A0] hover:text-white flex items-center gap-1 group"
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="overline text-[#FF2D87] mb-6">Company</h3>
              <ul className="space-y-4">
                {footerLinks.company.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer-link text-[#A0A0A0] hover:text-[#FF2D87] flex items-center gap-1 group"
                        data-testid="footer-support-us"
                      >
                        <Heart className="w-3 h-3" />
                        {link.label}
                        <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ) : link.href.startsWith("mailto") ? (
                      <a
                        href={link.href}
                        className="footer-link text-[#A0A0A0] hover:text-white flex items-center gap-1 group"
                      >
                        {link.label}
                        <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="footer-link text-[#A0A0A0] hover:text-white flex items-center gap-1 group"
                      >
                        {link.label}
                        <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="overline text-[#FF2D87] mb-6">Connect</h3>
              <div className="flex gap-4 mb-8">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border-2 border-[#4A4A4A] flex items-center justify-center hover:border-[#FF2D87] hover:text-[#FF2D87] transition-colors"
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Powered By Banner */}
      <div className="bg-[#5170FF]">
        <div className="container-main py-8">
          <a
            href="https://creativeensemblemedia.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 group"
            data-testid="powered-by-link"
          >
            <span className="text-sm text-white/70 group-hover:text-white transition-colors">A brand by</span>
            <span className="font-logo text-base uppercase text-[#FFC2E8] group-hover:text-white transition-colors" style={{letterSpacing: '-0.03em'}}>
              Creative Ensemble Media
            </span>
            <ArrowUpRight className="w-4 h-4 text-[#FFC2E8] group-hover:text-white transition-colors" />
          </a>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#2A2A2A]">
        <div className="container-main py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-[#A0A0A0]">
            © {currentYear} The Creative Ensemble. All rights reserved.
          </p>
          <div className="flex gap-6">
            {footerLinks.legal.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="text-sm text-[#A0A0A0] hover:text-white transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
